import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [sessionId, setSessionId] = useState(null);
  const [planName, setPlanName] = useState('');

  useEffect(() => {
    const session_id = searchParams.get('session_id');
    const plan = searchParams.get('plan');
    
    if (session_id) {
      setSessionId(session_id);
      setPlanName(plan || 'Premium');
      pollPaymentStatus(session_id, 0);
    } else {
      // Direct navigation without session_id
      setStatus('success');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('failed');
      toast.error('Payment verification timed out. Please check your email or contact support.');
      return;
    }

    try {
      // Wait a bit before checking
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Check if subscription is active by fetching user profile
      const response = await axios.get(`${API}/auth/me`);
      const tradieProfile = response.data.tradie_profile;
      
      if (tradieProfile && tradieProfile.current_tier !== 'basic') {
        // Upgraded successfully
        setStatus('success');
        setPlanName(tradieProfile.current_tier);
        toast.success('Subscription activated!');
        return;
      }
      
      // Still processing, continue polling
      pollPaymentStatus(sessionId, attempts + 1);
    } catch (error) {
      console.error('Failed to check payment status:', error);
      // Continue polling on error
      if (attempts < maxAttempts) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      } else {
        setStatus('failed');
      }
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-12 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-6">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto"></div>
            <h2 className="text-2xl font-bold">Processing your payment...</h2>
            <p className="text-muted-foreground">
              Please wait while we confirm your subscription.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
              <span>Verifying with Stripe</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-12 px-4">
        <Card className="max-w-md w-full border-destructive">
          <CardContent className="p-8 text-center space-y-6">
            <div className="text-6xl">⚠️</div>
            <h2 className="text-2xl font-bold">Payment Verification Issue</h2>
            <p className="text-muted-foreground">
              We couldn't verify your payment. Your subscription may still be processing.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full rounded-xl"
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/pricing')}
                className="w-full rounded-xl"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center py-12 px-4" data-testid="subscription-success-page">
      <Card className="max-w-md w-full border-2 border-green-500 shadow-lg">
        <CardContent className="p-8 text-center space-y-6">
          <div className="text-6xl">🎉</div>
          <h1 className="text-3xl font-bold">Subscription Successful!</h1>
          <Badge className="bg-green-500 text-white text-lg px-4 py-2 capitalize">
            {planName} Plan Active
          </Badge>
          <p className="text-muted-foreground">
            Your subscription is now active. You can start enjoying all the {planName} features.
          </p>
          
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-left">
            <h3 className="font-semibold mb-2 text-sm">What's Next?</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• Create more service listings</li>
              <li>• Upload more photos and videos</li>
              <li>• Reply to customer reviews</li>
              <li>• Pin your best reviews</li>
            </ul>
          </div>
          
          <Button
            onClick={() => navigate('/dashboard')}
            className="w-full rounded-xl bg-primary hover:bg-primary-hover shadow-lg"
            data-testid="go-to-dashboard-button"
          >
            Go to Dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;
